import { FC, useEffect, useRef, useState } from "react";
import RestrictedPage from "@/consumer/restrictedPage";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Grid,
  Tooltip,
  useMediaQuery,
  useTheme,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import DropDownSubjectSearch from "@/components/forms/inputs/dropDownSubjectSearch";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import { api } from "@/helper/api";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import SubjectOverviewDocuments from "@/components/subjectOverview/subjectOverviewDocuments";
import SubjectOverviewAppointments from "@/components/subjectOverview/subjectOverviewAppointments";
import SubjectOverviewNotes from "@/components/subjectOverview/subjectOverviewNotes";
import SubjectOverviewAppointmentRequests from "@/components/subjectOverview/subjectOverviewAppointmentRequests";
import SubjectOverviewTodos from "@/components/subjectOverview/subjectOverviewTodos";
import BuildingTable from "@/components/buildings/buildingTable";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useNavigate, useParams } from "react-router-dom";
import styles from "@/routes/admin/subjectCompanyOverview.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useDispatch } from "react-redux";
import { setSubjectSearch } from "@/components/state/subjectSearch/subjectSearchSlice";
import EditIcon from "@mui/icons-material/Edit";
import UserEditModal from "@/components/userAdministration/userEditModal";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import GenericNotification from "@/components/notification/genericNotification";
import EmailIcon from "@mui/icons-material/Email";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import Modal from "@/components/modal";
import FileUploadInput from "@/components/forms/inputs/upload";
import SubjectOverviewMailLog from "@/components/subjectOverview/subjectOverviewMaillog";
import Restricted from "@/consumer/restricted";
import MailPreferencesDialog from "@/components/mailTemplate/mailPreferencesDialog";
import SubjectOverviewEnrolments from "@/components/subjectOverview/subjectOverviewEnrolments";
import CompanyHierarchyModal from "@/components/company/companyHierarchyModal";
import { KaerIcon } from "@/icons/kaerIcon";
import SelectedSubjectDisplay from "@/components/general/selectedSubjectDisplay";
import SubjectOverviewAnchorDataCard from "@/components/subjectOverview/subjectOverviewAnchorDataCard";
import SubjectOverviewManageTab from "@/components/subjectOverview/subjectOverviewManageTab";
import SubjectOverviewUserInformationCard from "@/components/subjectOverview/subjectOverviewUserInformationCard";

interface Props {
  subdomain?: string;
}

const SubjectCompanyOverview: FC<Props> = ({ subdomain }) => {
  const { t } = useTranslation();
  usePageHeading(t("subject_company_overview"));
  const { subjectId: urlSubjectId } = useParams<{ subjectId?: string }>();
  //eslint-disable-next-line
  const [subjectOptions, setSubjectOptions] = useState<any>([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState<any>("");
  const [detailedSubject, setDetailedSubject] = useState<any>({});
  const { handleError, errorModal } = useErrorModal();
  const [formValuesForOverview, setFormValuesForOverview] = useState<any>([]);
  const [expandedAppointmentRequests, setExpandedAppointmentRequests] =
    useState<boolean>(false);
  const [expandedDocuments, setExpandedDocuments] = useState<boolean>(false);
  const [expandedTodos, setExpandedTodos] = useState<boolean>(false);
  const [expandedNotes, setExpandedNotes] = useState<boolean>(true);
  const [expandedAppointments, setExpandedAppointments] =
    useState<boolean>(true);
  const [expandedBuildings, setExpandedBuildings] = useState<boolean>(false);
  const [expandedMailLog, setExpandedMailLog] = useState<boolean>(false);
  const [expandedEnrolments, setExpandedEnrolments] = useState<boolean>(false);
  const [expandedManagerTab, setExpandedManagerTab] = useState<boolean>(false);

  const [openUserEditModal, setOpenUserEditModal] = useState<boolean>(false);
  const [mailPreferencesOpen, setMailPreferencesOpen] = useState(false);
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<any>("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info" | undefined
  >();
  const [modalUploadOpen, setModalUploadOpen] = useState<boolean>(false);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [includeDeletedSubjects, setIncludeDeletedSubjects] =
    useState<boolean>(false);
  const [hierarchyModalOpen, setHierarchyModalOpen] = useState<boolean>(false);
  const [selectedCompanyForHierarchy, setSelectedCompanyForHierarchy] =
    useState<{
      subjectId: string;
      companyId: string;
      companyName: string;
    } | null>(null);

  const overviewSubjectSearchRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const isManaging = detailedSubject?.company_management_set?.length !== 0;

  const { showConfirmationModalAsync } = useConfirmationModal();

  const activeSubjectId = urlSubjectId || selectedSubjectId;
  const activeSubjectIdRef = useRef<string | null>(null);
  const [subjectRefreshVersion, setSubjectRefreshVersion] = useState(0);

  const resetStateOnCloseNotification = () => {
    setShowNotification(false);
    setNotificationMessage("");
  };

  const handleSubjectSelect = (selectedOption: any) => {
    if (
      selectedOption &&
      typeof selectedOption === "object" &&
      selectedOption.value
    ) {
      setSelectedSubjectId(selectedOption.value);
      setDetailedSubject({});
    }
  };
  const getSubjectOverview = (subjectId: string) => {
    const additionalRouteParts: any = {};
    additionalRouteParts["overview"] = "/";
    api.genericApiRequest({
      method: "get",
      entity: "subject",
      entityId: subjectId,
      additionalRouteParts: additionalRouteParts,
      successHandler: (res: any) => {
        const data = res.data;
        setFormValuesForOverview(data);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };

  useEffect(() => {
    if (urlSubjectId && urlSubjectId !== selectedSubjectId) {
      setSelectedSubjectId(urlSubjectId);
    }
  }, [urlSubjectId, selectedSubjectId]);

  useEffect(() => {
    let isCurrent = true;
    activeSubjectIdRef.current = activeSubjectId;
    if (activeSubjectId !== "") {
      api.genericApiRequest({
        method: "get",
        entity: "subject",
        entityId: activeSubjectId,
        successHandler: (res: any) => {
          if (!isCurrent) return;
          setDetailedSubject(res.data);
          if (res.data.is_company === true) {
            getSubjectOverview(activeSubjectId);
          }
        },
        failHandler: (error: any) => {
          if (isCurrent) handleError(error);
        },
      });
    }
    return () => {
      isCurrent = false;
      activeSubjectIdRef.current = null;
    };
  }, [activeSubjectId, subjectRefreshVersion]);

  const resetSubject = () => {
    setDetailedSubject({});
    setSelectedSubjectId("");
  };

  const handleResetToSubjectSearch = () => {
    dispatch(setSubjectSearch({}));
    resetSubject();
    navigate("/subjectOverview");
  };

  const handleExpandClick = (item: any) => {
    if (item === "appointmentRequest") {
      setExpandedAppointmentRequests(!expandedAppointmentRequests);
    }
    if (item === "documents") {
      setExpandedDocuments(!expandedDocuments);
    }
    if (item === "todos") {
      setExpandedTodos(!expandedTodos);
    }
    if (item === "notes") {
      setExpandedNotes(!expandedNotes);
    }
    if (item === "appointments") {
      setExpandedAppointments(!expandedAppointments);
    }
    if (item === "buildings") {
      setExpandedBuildings(!expandedBuildings);
    }
    if (item === "mailLog") {
      setExpandedMailLog(!expandedMailLog);
    }
    if (item === "enrolments") {
      setExpandedEnrolments(!expandedEnrolments);
    }
    if (item === "managerTab") {
      setExpandedManagerTab(!expandedManagerTab);
    }
  };

  const renderServiceLevelChip = () => {
    if (
      detailedSubject &&
      detailedSubject.is_company &&
      detailedSubject.company?.support_type !== null
    ) {
      const serviceLevel = `support_type_${detailedSubject.company.support_type}`;
      return (
        <Tooltip title={t("service_level")} placement="left">
          <Chip label={t(serviceLevel)} className={styles.serviceLevelChip} />
        </Tooltip>
      );
    }
  };
  const handleUserEditModal = (status: boolean) => {
    setOpenUserEditModal(status);
  };

  const renderCustomerButtons = () => {
    if (!detailedSubject?.id) return null;

    const isSubjectLoaded = Boolean(detailedSubject?.id);

    if (detailedSubject.is_company) {
      return (
        <div
          className={styles.isCompanyButtonsContainer}
          data-testid="company-actions"
        >
          {renderServiceLevelChip()}
          <div className={styles.companyButtonsContainer}>
            <GenericButton
              startIcon={
                <KaerIcon
                  icon="companyHierarchy"
                  color="#ffffff"
                  width="1.5rem"
                  height="1.5rem"
                />
              }
              onClick={() => {
                setSelectedCompanyForHierarchy({
                  subjectId: detailedSubject.id,
                  companyId: detailedSubject.company?.id,
                  companyName: detailedSubject.name,
                });
                setHierarchyModalOpen(true);
              }}
            >
              {t("hierarchy")}
            </GenericButton>
            <GenericButton
              startIcon={
                <KaerIcon
                  icon="employee"
                  color="#ffffff"
                  width="1.5rem"
                  height="1.5rem"
                />
              }
              disabled={!isSubjectLoaded}
              onClick={() => {
                window.open(
                  `/admin/userAdministration/${detailedSubject.id}`,
                  "_blank",
                  "noopener,noreferrer"
                );
              }}
            >
              {t("customer")}
            </GenericButton>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderCustomerEditButton = () => {
    if (
      !detailedSubject?.id ||
      detailedSubject?.is_company !== false ||
      !detailedSubject?.company?.id
    ) {
      return null;
    }

    return (
      <Tooltip title={t("edit_employee_information")} placement="top">
        <GenericButton
          variant="icon"
          aria-label={t("edit_employee_information")}
          onClick={() => handleUserEditModal(true)}
        >
          <EditIcon />
        </GenericButton>
      </Tooltip>
    );
  };

  const renderFileUploadButton = () => {
    const isDetailedSubjectValid =
      detailedSubject &&
      typeof detailedSubject.is_company === "boolean" &&
      typeof detailedSubject.is_registered === "boolean" &&
      typeof detailedSubject.is_analog === "boolean";

    if (!isDetailedSubjectValid) {
      return null;
    }

    let buttonTitle = "";
    if (!detailedSubject?.is_registered) {
      buttonTitle = t("subject_not_registered_warning");
    }

    if (detailedSubject?.is_analog && !detailedSubject?.is_registered) {
      buttonTitle = t("evk_missing_please_upload");
    }
    if (!detailedSubject.is_registered) {
      return (
        <Grid item>
          <Tooltip title={t("upload")} placement="top-end">
            <GenericButton
              variant="contained"
              color="error"
              onClick={() => setModalUploadOpen(true)}
              endIcon={<FileUploadIcon />}
            >
              {buttonTitle}
            </GenericButton>
          </Tooltip>
        </Grid>
      );
    }
    return null;
  };
  const renderSendRegistrationMailButton = () => {
    const isDetailedSubjectValid =
      detailedSubject &&
      typeof detailedSubject.is_company === "boolean" &&
      typeof detailedSubject.is_registered === "boolean" &&
      typeof detailedSubject.is_analog === "boolean";

    if (!isDetailedSubjectValid) {
      return null;
    }

    const handleSendRegistrationEmail = async (
      skipQuestion?: false | undefined
    ) => {
      if (!skipQuestion) {
        const isConfirmed = await showConfirmationModalAsync(
          t("send_registration_email_question", {
            email: detailedSubject?.email,
          })
        );
        if (!isConfirmed) return;
      }
      const additionalRouteParts: any = {};
      additionalRouteParts["send_user_registration_email"] = "/";

      api.genericApiRequest({
        entity: "subject",
        method: "post",
        submitData: {},
        successHandler: () => {
          setNotificationMessage(t("successfully_sent"));
          setNotificationVariant("success");
          setShowNotification(true);
        },
        failHandler: (error: any) => {
          handleError(error);
        },
        entityId: detailedSubject?.id,
        additionalRouteParts: additionalRouteParts,
      });
    };
    if (!detailedSubject.is_registered && !detailedSubject.is_analog) {
      return (
        <Grid item>
          <Tooltip title={t("send_registration_email")} placement="top-end">
            <GenericButton
              variant="contained"
              color="tertiary"
              onClick={() => handleSendRegistrationEmail(false)}
              endIcon={<EmailIcon />}
            >
              {t("send_registration_email")}
            </GenericButton>
          </Tooltip>
        </Grid>
      );
    }
    return null;
  };

  const handleCopyEmail = () => {
    if (!detailedSubject?.email) return;
    navigator.clipboard.writeText(detailedSubject.email);
    setNotificationMessage(t("email_copied"));
    setNotificationVariant("success");
    setShowNotification(true);
  };

  const renderCopyEmailButton = () => {
    if (detailedSubject?.is_company !== false || !detailedSubject?.email) {
      return null;
    }
    return (
      <Tooltip title={t("copy_email")} placement="top">
        <GenericButton
          variant="icon"
          aria-label={t("copy_email")}
          onClick={handleCopyEmail}
        >
          <ContentCopyIcon />
        </GenericButton>
      </Tooltip>
    );
  };

  const handleSubjectId = (id: string) => {
    setSelectedSubjectId(id);
  };

  const handleNotificationMessage = (value: any) => {
    setNotificationMessage(value);
  };
  const handleNotificationVariant = (value: any) => {
    setNotificationVariant(value);
  };
  const handleShowNotification = (state: boolean) => {
    setShowNotification(state);
  };

  return (
    <RestrictedPage permissionRequired="frontend_view_subject_company_overview">
      {/*MainContainer _________________________________________________________________________________________________ */}
      <Grid container className={styles.mainContainer}>
        <Grid item container xs={12} columnSpacing={"1rem"} rowSpacing={"1rem"}>
          <Grid item xs={isMobile ? 12 : isTablet ? 7 : 6}>
            {urlSubjectId ? (
              <SelectedSubjectDisplay
                subjectName={
                  detailedSubject?.name
                    ? `${detailedSubject.name}
                    ${
                      detailedSubject?.email
                        ? ` (${detailedSubject.email})`
                        : ""
                    }`
                    : ""
                }
                onReset={handleResetToSubjectSearch}
              />
            ) : (
              <div>
                <FormFieldLabel
                  label={t("subject")}
                  htmlFor="subject-company-overview-subject"
                />
                <DropDownSubjectSearch
                  name="subject-company-overview-subject"
                  options={subjectOptions}
                  onChange={handleSubjectSelect}
                  subdomain={subdomain}
                  useGlobalState={true}
                  isClearable={true}
                  resetInput={resetSubject}
                  customRef={overviewSubjectSearchRef}
                  autofocus={true}
                  includeDeletedSubjects={includeDeletedSubjects}
                />
              </div>
            )}
          </Grid>
          <Grid
            item
            xs={isMobile ? 12 : isTablet ? 5 : 3}
            className={styles.checkboxContainer}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeDeletedSubjects}
                  onChange={(e) => setIncludeDeletedSubjects(e.target.checked)}
                  color="primary"
                />
              }
              label={t("include_deleted_subjects")}
            />
            {renderCopyEmailButton()}
            {renderCustomerEditButton()}
            {subdomain === "admin" &&
              detailedSubject?.id &&
              !detailedSubject.is_company && (
                <Restricted permissionRequired="change_mailtemplate">
                  <GenericButton
                    variant="outlined"
                    title={t("mail_preferences_title")}
                    startIcon={<SettingsOutlinedIcon />}
                    onClick={() => setMailPreferencesOpen(true)}
                  >
                    {t("mail_preferences_button")}
                  </GenericButton>
                </Restricted>
              )}
          </Grid>
          {activeSubjectId !== "" && !isMobile && (
            <>
              {!isTablet && (
                <Grid
                  container
                  item
                  xs={3}
                  className={styles.buttonContainer}
                  columnSpacing={"0.25rem"}
                >
                  {renderCustomerButtons()}
                </Grid>
              )}

              <Grid
                container
                item
                xs={isTablet ? 12 : 7}
                className={styles.warnings_buttonContainer}
                columnSpacing={"0.25rem"}
              >
                {renderSendRegistrationMailButton()}
                {renderFileUploadButton()}
                {isTablet && renderCustomerButtons()}
              </Grid>
            </>
          )}
          {activeSubjectId !== "" && isMobile && (
            <Grid
              container
              columns={1}
              item
              xs={12}
              className={styles.buttonContainer}
              columnSpacing={"0.25rem"}
              rowSpacing={"0.25rem"}
            >
              {detailedSubject?.is_company && (
                <Grid container item xs={12}>
                  {renderCustomerButtons()}
                </Grid>
              )}
              <Grid item xs={12}>
                {renderSendRegistrationMailButton()}
              </Grid>
              <Grid item xs={12}>
                {renderFileUploadButton()}
              </Grid>
            </Grid>
          )}
        </Grid>
        {Object.keys(detailedSubject).length !== 0 && (
          <>
            {/* Container 1 - Subject Overview Card_________________________________________________________________________________________________ */}
            {detailedSubject.is_company && (
              <Grid
                container
                columnSpacing={"1rem"}
                // className={styles.container1}
              >
                <Grid item xs={12} className={styles.fullHeight}>
                  <SubjectOverviewAnchorDataCard
                    formValues={formValuesForOverview}
                    subjectId={detailedSubject.id}
                    isCompany={detailedSubject.is_company}
                    handleError={handleError}
                    handleNotificationMessage={handleNotificationMessage}
                    handleNotificationVariant={handleNotificationVariant}
                    handleShowNotification={handleShowNotification}
                    onSuccessfullSubmit={getSubjectOverview}
                  />
                </Grid>
              </Grid>
            )}
            {/* _________________________________________________________________________________________________ */}
            {/* Container 1b - User Information Card_________________________________________________________________________________________________ */}
            {!detailedSubject.is_company && (
              <Grid container>
                <Grid item xs={12}>
                  <SubjectOverviewUserInformationCard
                    detailedSubject={detailedSubject}
                    handleError={handleError}
                  />
                </Grid>
              </Grid>
            )}
            {/* _________________________________________________________________________________________________ */}
            {/*Container 2 - Notes and Appointments_________________________________________________________________________________________________ */}
            <Grid
              container
              columnSpacing={"1rem"}
              rowSpacing={isTablet ? "1rem" : ""}
            >
              <Grid item xs={isTablet ? 12 : 7}>
                <Accordion
                  defaultExpanded={true}
                  className={
                    expandedNotes ? "MuiPaperRootCollapse" : "borderRadius"
                  }
                  expanded={expandedNotes}
                  onChange={() => handleExpandClick("notes")}
                  square={true}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <h5 className="no-margin">{t("notes")}</h5>{" "}
                  </AccordionSummary>
                  <AccordionDetails>
                    <SubjectOverviewNotes detailedSubject={detailedSubject} />
                  </AccordionDetails>
                </Accordion>
              </Grid>
              <Grid item xs={isTablet ? 12 : 5}>
                <Accordion
                  square={true}
                  defaultExpanded={true}
                  className={
                    expandedAppointments
                      ? "MuiPaperRootCollapse"
                      : "borderRadius"
                  }
                  expanded={expandedAppointments}
                  onChange={() => handleExpandClick("appointments")}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <h5 className="no-margin">{t("appointment_multi")}</h5>
                  </AccordionSummary>
                  <AccordionDetails>
                    <SubjectOverviewAppointments
                      detailedSubject={detailedSubject}
                      openWithSpecificSubjectIdUrl={Boolean(urlSubjectId)}
                    />
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>
            {/* _________________________________________________________________________________________________ */}
            {/* Container 3 - Appointment Requests_________________________________________________________________________________________________ */}
            <Grid container>
              <Grid item xs={12}>
                <Accordion
                  square={true}
                  className="MuiPaperRootCollapse"
                  expanded={expandedAppointmentRequests}
                  onChange={() => {
                    handleExpandClick("appointmentRequest");
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <h5 className="no-margin">
                      {t("appointmentrequest_multi")}
                    </h5>
                  </AccordionSummary>
                  <AccordionDetails>
                    <SubjectOverviewAppointmentRequests
                      detailedSubject={detailedSubject}
                      openWithSpecificSubjectIdUrl={Boolean(urlSubjectId)}
                    />
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>
            {/* _________________________________________________________________________________________________ */}
            {/* Container 4 - Documents and Todos_________________________________________________________________________________________________ */}
            <Grid
              container
              columnSpacing={"1rem"}
              rowSpacing={isTablet ? "1rem" : ""}
            >
              <Grid item xs={isTablet ? 12 : 7}>
                <Accordion
                  square={true}
                  className={
                    expandedDocuments ? "MuiPaperRootCollapse" : "borderRadius"
                  }
                  expanded={expandedDocuments}
                  onChange={() => {
                    handleExpandClick("documents");
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <h5 className="no-margin">{t("documents")}</h5>
                  </AccordionSummary>
                  <AccordionDetails>
                    <SubjectOverviewDocuments
                      subjectId={detailedSubject.id}
                      subjectForDispatch={
                        urlSubjectId ? undefined : detailedSubject
                      }
                      openWithSpecificSubjectIdUrl={Boolean(urlSubjectId)}
                    />
                  </AccordionDetails>
                </Accordion>
              </Grid>

              <Grid item xs={isTablet ? 12 : 5}>
                <Accordion
                  square={true}
                  className={
                    expandedTodos ? "MuiPaperRootCollapse" : "borderRadius"
                  }
                  expanded={expandedTodos}
                  onChange={() => {
                    handleExpandClick("todos");
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <h5 className="no-margin">{t("todo_multi")}</h5>
                  </AccordionSummary>
                  <AccordionDetails>
                    <SubjectOverviewTodos
                      detailedSubject={detailedSubject}
                      openWithSpecificSubjectIdUrl={Boolean(urlSubjectId)}
                    />
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>
            {/* _________________________________________________________________________________________________ */}
            {/* Container 5 - Buildings Table _________________________________________________________________________________________________ */}
            {detailedSubject.is_company && (
              <Grid container>
                <Grid item xs={12}>
                  <Accordion
                    square={true}
                    className={
                      expandedBuildings
                        ? "MuiPaperRootCollapse"
                        : "borderRadius"
                    }
                    expanded={expandedBuildings}
                    onChange={() => handleExpandClick("buildings")}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <h5 className="no-margin">{t("buildings")}</h5>
                    </AccordionSummary>
                    <AccordionDetails>
                      <BuildingTable
                        subjectId={detailedSubject.id}
                        hideBackButton
                        hideTableHeader
                      />
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              </Grid>
            )}
            {/* _________________________________________________________________________________________________ */}
            {/* Container Maillog*/}
            {!detailedSubject.is_company && (
              <Grid container>
                <Grid item xs={12}>
                  <Accordion
                    square={true}
                    className="MuiPaperRootCollapse"
                    expanded={expandedMailLog}
                    onChange={() => {
                      handleExpandClick("mailLog");
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <h5 className="no-margin">{t("email")}</h5>
                    </AccordionSummary>
                    <AccordionDetails>
                      <SubjectOverviewMailLog
                        detailedSubject={detailedSubject}
                      />
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              </Grid>
            )}
            {/* Container Enrolments */}
            {!detailedSubject.is_company && (
              <Grid container>
                <Grid item xs={12}>
                  <Accordion
                    square={true}
                    className="MuiPaperRootCollapse"
                    expanded={expandedEnrolments}
                    onChange={() => {
                      handleExpandClick("enrolments");
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <h5 className="no-margin">{t("course_enrolments")}</h5>
                    </AccordionSummary>
                    <AccordionDetails>
                      <SubjectOverviewEnrolments
                        detailedSubject={detailedSubject}
                      />
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              </Grid>
            )}
            {isManaging && (
              <Grid container>
                <Grid item xs={12}>
                  <Accordion
                    square={true}
                    className="MuiPaperRootCollapse"
                    expanded={expandedManagerTab}
                    onChange={() => {
                      handleExpandClick("managerTab");
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <h5 className="no-margin">{t("manager")}</h5>
                    </AccordionSummary>
                    <AccordionDetails>
                      <SubjectOverviewManageTab
                        detailedSubject={detailedSubject}
                      />
                    </AccordionDetails>
                  </Accordion>{" "}
                </Grid>
              </Grid>
            )}
          </>
        )}
      </Grid>
      <Modal
        open={modalUploadOpen}
        onClose={() => setModalUploadOpen(false)}
        title={t("upload_file")}
        hideDialogActions={true}
        disableCancelButton={uploadInProgress}
        maxWidth="40rem"
      >
        <FileUploadInput
          multiple={true}
          subjectId={detailedSubject?.id}
          folderId={"00000026-1111-0000-0000-000000000000"}
          name="file"
          evkUpload={true}
          documentsForAppointmentUpload={false}
          onUploadStateChange={setUploadInProgress}
          onCancel={() => {
            setModalUploadOpen(false);
          }}
          handleUploadSuccess={(closeModal: boolean) => {
            if (activeSubjectIdRef.current !== activeSubjectId) return;
            setSubjectRefreshVersion((version) => version + 1);
            if (closeModal) {
              setModalUploadOpen(false);
            }
          }}
        />
      </Modal>
      {openUserEditModal && (
        <UserEditModal
          open={openUserEditModal}
          onClose={() => handleUserEditModal(false)}
          companySubjectId={detailedSubject?.company?.subject_id}
          userSubjectId={detailedSubject?.id}
          handleResetSubject={resetSubject}
          handleSubjectId={handleSubjectId}
        />
      )}
      {mailPreferencesOpen && detailedSubject?.id && (
        <MailPreferencesDialog
          key={detailedSubject.id}
          subjectId={detailedSubject.id}
          onClose={() => setMailPreferencesOpen(false)}
        />
      )}
      {errorModal}
      <GenericNotification
        message={showNotification ? notificationMessage : null}
        variant={notificationVariant}
        handleCloseSnackbar={resetStateOnCloseNotification}
      />
      {selectedCompanyForHierarchy && (
        <CompanyHierarchyModal
          open={hierarchyModalOpen}
          onClose={() => {
            setHierarchyModalOpen(false);
            setSelectedCompanyForHierarchy(null);
          }}
          subjectId={selectedCompanyForHierarchy.subjectId}
          selectedCompanyId={selectedCompanyForHierarchy.companyId}
          selectedSubjectId={selectedCompanyForHierarchy.subjectId}
          disableClickForHighlightedCompany={true}
        />
      )}
    </RestrictedPage>
  );
};

export default SubjectCompanyOverview;
